// components/Testimonials.tsx

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { testimonials } from "../../data/testimonials";

const Testimonials: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );
  };

  return (
    <section className="relative py-16 bg-gradient-to-b from-white to-[#FAFAFA] overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#8B52FF]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#E05A6A]/5 rounded-full blur-[120px]" />

      {/* Large quote decoration */}
      <div className="absolute top-20 left-10 opacity-[0.03] hidden lg:block">
        <Quote className="w-64 h-64 text-[#8B52FF]" />
      </div>
      <div className="absolute bottom-20 right-10 opacity-[0.03] hidden lg:block rotate-180">
        <Quote className="w-48 h-48 text-[#E05A6A]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10 text-sm font-medium text-[#8B52FF] mb-4"
          >
            Testimonials
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            What Our{" "}
            <span className="bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] bg-clip-text text-transparent">
              Contributors Say
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Hear from experts around the world who are shaping the future of AI
          </p>
        </motion.div>

        {/* Main Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl shadow-[#8B52FF]/5 border border-gray-100">
                {/* Quote icon */}
                <div className="absolute -top-5 left-10">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] flex items-center justify-center shadow-lg">
                    <Quote className="w-5 h-5 text-white" />
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                  {/* Left - Avatar & Info */}
                  <div className="flex flex-col items-center lg:items-start lg:min-w-[200px]">
                    {/* Avatar */}
                    <div className="relative mb-4">
                      <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white text-2xl lg:text-3xl font-bold shadow-xl">
                        {testimonials[activeIndex].avatar}
                      </div>
                      {/* Online indicator */}
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-500 border-4 border-white" />
                    </div>

                    {/* Name & Role */}
                    <h4 className="text-xl font-bold text-gray-900 text-center lg:text-left">
                      {testimonials[activeIndex].name}
                    </h4>
                    <p className="text-[#8B52FF] font-medium mb-1">
                      {testimonials[activeIndex].role}
                    </p>
                    <p className="text-gray-500 text-sm mb-4">
                      {testimonials[activeIndex].location}
                    </p>

                    {/* Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonials[activeIndex].rating)].map(
                        (_, i) => (
                          <Star
                            key={i}
                            className="w-5 h-5 fill-[#E05A6A] text-[#E05A6A]"
                          />
                        )
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex gap-4 lg:flex-col lg:gap-2">
                      {testimonials[activeIndex].earnings && (
                        <div className="text-center lg:text-left">
                          <div className="text-lg font-bold text-gray-900">
                            {testimonials[activeIndex].earnings}
                          </div>
                          <div className="text-xs text-gray-500">Earned</div>
                        </div>
                      )}
                      {testimonials[activeIndex].projectsCompleted && (
                        <div className="text-center lg:text-left">
                          <div className="text-lg font-bold text-gray-900">
                            {testimonials[activeIndex].projectsCompleted}
                          </div>
                          <div className="text-xs text-gray-500">Projects</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right - Quote */}
                  <div className="flex-1">
                    <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-6 italic">
                      "{testimonials[activeIndex].quote}"
                    </p>

                    {/* Expertise tag */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#8B52FF]/10 to-[#E05A6A]/10">
                      <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#8B52FF] to-[#E05A6A]" />
                      <span className="text-sm font-medium text-gray-700">
                        Expert in {testimonials[activeIndex].expertise}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#8B52FF] hover:text-[#8B52FF] transition-colors shadow-sm"
            >
              <ChevronLeft className="w-5 h-5" />
            </motion.button>

            {/* Dots indicator */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === activeIndex
                      ? "w-8 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A]"
                      : "w-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:border-[#8B52FF] hover:text-[#8B52FF] transition-colors shadow-sm"
            >
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        {/* Mini testimonials grid */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => setActiveIndex(index)}
              className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                activeIndex === index
                  ? "bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 border-2 border-[#8B52FF]/30"
                  : "bg-white border border-gray-100 hover:border-[#8B52FF]/20 hover:shadow-lg"
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center text-white text-sm font-bold">
                  {testimonial.avatar}
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 text-sm">
                    {testimonial.name}
                  </h5>
                  <p className="text-xs text-gray-500">{testimonial.role}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">
                "{testimonial.quote.substring(0, 80)}..."
              </p>
            </motion.div>
          ))}
        </motion.div> */}
      </div>
    </section>
  );
};

export default Testimonials;
